import { useState, useMemo, useCallback } from 'react';
import { TAPE_PACKAGES } from 'types/clientV2/stock';
import {
  CountingValidationType,
  ValidationWithStatus,
  CountingValidation,
  ValidationParams,
  CountingValidationNameIds,
} from 'views/warehouse/Counting/hooks/constants';

const COUNTING_VALIDATIONS = {
  [CountingValidationNameIds.RECEIVE_OFF_BY_NEGATIVE]: {
    id: CountingValidationNameIds.RECEIVE_OFF_BY_NEGATIVE,
    type: CountingValidationType.ERROR,
    name: 'Receive Off By Negative',
    description: 'The counted quantity is less than the expected quantity.',
    secondConfirmationDescription:
      'Are you sure this is correct? This will create a new purchase line and notify the procurement team. This cannot be undone.',
    requiresDoubleConfirmation: true,
  },
  [CountingValidationNameIds.RECEIVE_OFF_BY_POSITIVE]: {
    id: CountingValidationNameIds.RECEIVE_OFF_BY_POSITIVE,
    type: CountingValidationType.WARNING,
    name: 'Receive Off By Positive',
    description:
      'The counted quantity is more than the expected quantity by more than 1.',
  },
  [CountingValidationNameIds.PASSIVE_HANDLING_OFF_BY_5_PLUS]: {
    id: CountingValidationNameIds.PASSIVE_HANDLING_OFF_BY_5_PLUS,
    type: CountingValidationType.ERROR,
    name: 'Count Off By 5+',
    description:
      'The component count is off by 5 or more from the expected quantity.',
    secondConfirmationDescription:
      'This is a significant discrepancy. Are you absolutely certain this count is correct?',
    requiresDoubleConfirmation: true,
  },
  [CountingValidationNameIds.PASSIVE_HANDLING_OFF_BY_LESS_THAN_5]: {
    id: CountingValidationNameIds.PASSIVE_HANDLING_OFF_BY_LESS_THAN_5,
    type: CountingValidationType.WARNING,
    name: 'Count Off By < 5',
    description:
      'The component count is off by less than 5 from the expected quantity.',
  },
  [CountingValidationNameIds.NON_PASSIVE_HANDLING_ANY_DISCREPANCY]: {
    id: CountingValidationNameIds.NON_PASSIVE_HANDLING_ANY_DISCREPANCY,
    type: CountingValidationType.ERROR,
    name: 'Handling Discrepancy',
    description: 'Component count does not match the expected quantity.',
    secondConfirmationDescription:
      'Any discrepancy for non-passive components is significant. Are you absolutely certain this count is correct?',
    requiresDoubleConfirmation: true,
  },
  [CountingValidationNameIds.NULL_TAPE_SIZE]: {
    id: CountingValidationNameIds.NULL_TAPE_SIZE,
    type: CountingValidationType.WARNING,
    name: 'Null Tape Size',
    description: 'The tape size is null.',
  },
};

/**
 * Helper function to create a validation with default status fields
 */
const createValidationWithStatus = (
  validation: CountingValidation,
  stockLotId: string,
  overrides: Partial<CountingValidation> = {}
): ValidationWithStatus => ({
  ...validation,
  ...overrides,
  isResolved: false,
  resolutionLevel: 0,
  stockLotId,
});

/**
 * Validation rule type for more declarative validation logic
 */
type ValidationRule = {
  condition: (params: ValidationParams, difference: number) => boolean;
  createValidation: (
    params: ValidationParams,
    difference: number
  ) => ValidationWithStatus;
};

/**
 * Flat list of validation rules in priority order
 */
const validationRules: ValidationRule[] = [
  // Receive: Error for any negative difference
  {
    condition: ({ isFromReceive = false }, difference) =>
      isFromReceive && difference < 0,
    createValidation: ({ stockLotId }) =>
      createValidationWithStatus(
        COUNTING_VALIDATIONS[CountingValidationNameIds.RECEIVE_OFF_BY_NEGATIVE],
        stockLotId
      ),
  },

  // Receive: Warning for positive difference over 1
  {
    condition: ({ isFromReceive = false }, difference) =>
      isFromReceive && difference > 1,
    createValidation: ({ stockLotId }) =>
      createValidationWithStatus(
        COUNTING_VALIDATIONS[CountingValidationNameIds.RECEIVE_OFF_BY_POSITIVE],
        stockLotId
      ),
  },

  // Handling Non-Passive: Error for any discrepancy
  {
    condition: (
      { isFromHandlingRequest = false, isPassive = true },
      difference
    ) => isFromHandlingRequest && !isPassive && difference !== 0,
    createValidation: ({ stockLotId, handlingRequestType = '' }) =>
      createValidationWithStatus(
        COUNTING_VALIDATIONS[
          CountingValidationNameIds.NON_PASSIVE_HANDLING_ANY_DISCREPANCY
        ],
        stockLotId,
        {
          name: `${handlingRequestType} Handling Discrepancy`,
          description: `${handlingRequestType} Handling Task: Stock lot quantity does not match what is expected.`,
        }
      ),
  },

  // Handling Passive: Error for discrepancy of 5 or more
  {
    condition: (
      { isFromHandlingRequest = false, isPassive = true },
      difference
    ) => isFromHandlingRequest && isPassive && Math.abs(difference) >= 5,
    createValidation: ({ stockLotId, handlingRequestType = '' }) =>
      createValidationWithStatus(
        COUNTING_VALIDATIONS[
          CountingValidationNameIds.PASSIVE_HANDLING_OFF_BY_5_PLUS
        ],
        stockLotId,
        {
          name: `${handlingRequestType} Count Off By 5+`,
          description: `${handlingRequestType} Handling Task: The component count is off by 5 or more from the expected quantity.`,
        }
      ),
  },

  // Handling Passive: Warning for discrepancy less than 5
  {
    condition: (
      { isFromHandlingRequest = false, isPassive = true },
      difference
    ) =>
      isFromHandlingRequest &&
      isPassive &&
      difference !== 0 &&
      Math.abs(difference) < 5,
    createValidation: ({ stockLotId, handlingRequestType = '' }) =>
      createValidationWithStatus(
        COUNTING_VALIDATIONS[
          CountingValidationNameIds.PASSIVE_HANDLING_OFF_BY_LESS_THAN_5
        ],
        stockLotId,
        {
          name: `${handlingRequestType} Count Off By < 5`,
          description: `${handlingRequestType} Handling Task: The component count is off by less than 5 from the expected quantity.`,
        }
      ),
  },

  // Tape Size: Warning for null tape size AND package is one of TAPE_PACKAGES
  {
    condition: ({ package: packageOption, tapeSize }) =>
      packageOption && TAPE_PACKAGES.includes(packageOption) && !tapeSize,
    createValidation: ({ stockLotId }) =>
      createValidationWithStatus(
        COUNTING_VALIDATIONS[CountingValidationNameIds.NULL_TAPE_SIZE],
        stockLotId
      ),
  },
];

const validateCountedStockLot = (
  params: ValidationParams
): ValidationWithStatus | null => {
  const { countedQuant = 0, expectedQuant } = params;

  if (countedQuant === undefined) return null;

  const difference = countedQuant - expectedQuant;

  const matchingRule = validationRules.find((rule) =>
    rule.condition(params, difference)
  );

  return matchingRule
    ? matchingRule.createValidation(params, difference)
    : null;
};

export const useCountingValidation = () => {
  const [validations, setValidations] = useState<ValidationWithStatus[]>([]);

  const handleValidateCountedStockLot = useCallback(
    ({
      countedQuant,
      expectedQuant,
      isPassive = true,
      isFromReceive = false,
      stockLotId,
      isFromHandlingRequest = false,
      handlingRequestType = '',
      package: packageOption,
      tapeSize,
    }: ValidationParams) => {
      const newValidation = validateCountedStockLot({
        countedQuant,
        expectedQuant,
        isPassive,
        isFromReceive,
        stockLotId,
        isFromHandlingRequest,
        handlingRequestType,
        package: packageOption,
        tapeSize,
      });

      if (newValidation) {
        setValidations((prev) => [
          ...prev.filter((v) => v.stockLotId !== newValidation.stockLotId),
          newValidation,
        ]);
      }
      return newValidation;
    },
    []
  );

  const resolveValidation = useCallback(
    (stockLotId: string): ValidationWithStatus | undefined => {
      const existingValidation = validations.find(
        (v) => v.stockLotId === stockLotId
      );
      if (!existingValidation) return undefined;

      const updatedValidation =
        existingValidation.requiresDoubleConfirmation &&
        existingValidation.type === CountingValidationType.ERROR
          ? {
              ...existingValidation,
              resolutionLevel: existingValidation.resolutionLevel + 1,
              isResolved: existingValidation.resolutionLevel + 1 >= 2,
            }
          : {
              ...existingValidation,
              isResolved: true,
              resolutionLevel: 2,
            };

      setValidations((prev) =>
        prev.map((validation) =>
          validation.stockLotId === stockLotId ? updatedValidation : validation
        )
      );

      return updatedValidation;
    },
    [validations]
  );

  const deleteValidation = useCallback((stockLotId: string) => {
    setValidations((prev) => prev.filter((v) => v.stockLotId !== stockLotId));
  }, []);

  const getValidationsForStockLot = useCallback(
    (stockLotId: string) =>
      validations.filter((v) => v.stockLotId === stockLotId),
    [validations]
  );

  const unresolvedValidations = useMemo(
    () => validations.filter((v) => !v.isResolved),
    [validations]
  );

  return {
    validations,
    unresolvedValidations,
    handleValidateCountedStockLot,
    resolveValidation,
    deleteValidation,
    getValidationsForStockLot,
  };
};
